/**
 * Event windows and the staff-reminder ladder, computed from an IST calendar
 * day + the wedding's canonical `slot_type` — TZ-INDEPENDENT.
 *
 * WHY: every old reminder ladder did `weddingDate − 4h` / `+ 12h` on the naive
 * midnight `wedding_date`, so the "4 hours before" reminder went out at 01:30 IST
 * and the "thank you" at 17:30 IST for every wedding (incident 2026-09-26). The
 * wedding's real hours live only in its slot (SLOT_WINDOWS).
 *
 * All maths here is `Date.UTC` on the parsed date-key parts, then −330 min
 * (IST is UTC+5:30, no DST). NEVER `new Date(y, m, d, h)` / `setHours` — those
 * depend on the process TZ (prod Asia/Kolkata, dev Mac Asia/Dubai, tests anything).
 */
import { SLOT_WINDOWS, slotTimeRange, type SlotType } from './timeSlot';

export type StaffReminderType =
  | 'reminder_30d'
  | 'reminder_15d'
  | 'reminder_7d'
  | 'reminder_1d'
  | 'reminder_4h'
  | 'thank_you';

const IST_OFFSET_MIN = 330;
const MIN = 60_000;
const HOUR = 60 * MIN;
/** Calendar-day reminders go out at this IST hour on (event date − N days). */
const DAY_REMINDER_HOUR_IST = 9;
/** How long after the window ends a late thank_you is still worth sending. */
const THANK_YOU_GRACE_MS = 24 * HOUR;

/** Allowed drift between a stored scheduled_for and the recomputed time before a row counts as stale. */
export const REMINDER_DRIFT_MS = 30 * 60 * 1000;

function parseDateKey(dateKey: string): { y: number; m: number; d: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey ?? ''));
  if (!match) throw new Error(`eventWindow: malformed date key ${JSON.stringify(dateKey)} (want YYYY-MM-DD)`);
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) {
    throw new Error(`eventWindow: impossible date ${dateKey}`);
  }
  return { y, m, d };
}

/** 'YYYY-MM-DD' (IST calendar day) + IST hh:mm → the UTC instant. TZ-independent. Throws on a malformed/impossible key. */
export function istInstant(dateKey: string, hour: number, minute = 0): Date {
  const { y, m, d } = parseDateKey(dateKey);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) throw new Error(`eventWindow: invalid hour ${hour}`);
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) throw new Error(`eventWindow: invalid minute ${minute}`);
  return new Date(Date.UTC(y, m - 1, d, hour, minute) - IST_OFFSET_MIN * MIN);
}

/** Event window for a wedding day + slot, as UTC instants. */
export function eventWindow(dateKey: string, slot: SlotType): { start: Date; end: Date } {
  const w = SLOT_WINDOWS[slot];
  if (!w) throw new Error(`eventWindow: unknown slot ${JSON.stringify(slot)}`);
  return { start: istInstant(dateKey, w.startHour), end: istInstant(dateKey, w.endHour) };
}

/** "6:00 PM – 10:00 PM" from slotTimeRange (display text; callers add " IST"). */
export function slotLabel(slot: SlotType): string {
  return slotTimeRange(slot).replace(' to ', ' – ');
}

/**
 * employee_bookings.event_start_time / event_end_time for a wedding's canonical `slot_type`:
 * the two halves of slotTimeRange ("6:00 PM" / "10:00 PM"). Returns null for a missing or
 * non-canonical slot — callers write empty times and log, never a guessed evening.
 */
export function slotBookingTimes(slot: string | null | undefined): { start: string; end: string } | null {
  if (slot !== 'day' && slot !== 'evening') return null;
  const [start, end] = slotTimeRange(slot).split(' to ');
  return start && end ? { start, end } : null;
}

/** 09:00 IST on (dateKey − days). Date.UTC normalises day underflow across months/years. */
function daysBeforeAt9(dateKey: string, days: number): Date {
  const { y, m, d } = parseDateKey(dateKey);
  return new Date(Date.UTC(y, m - 1, d - days, DAY_REMINDER_HOUR_IST, 0) - IST_OFFSET_MIN * MIN);
}

/** 30d/15d/7d/1d at 09:00 IST on (date − N days); reminder_4h = start − 4h; thank_you = end + 1h. */
export function staffReminderSchedule(dateKey: string, slot: SlotType): Record<StaffReminderType, Date> {
  const { start, end } = eventWindow(dateKey, slot);
  return {
    reminder_30d: daysBeforeAt9(dateKey, 30),
    reminder_15d: daysBeforeAt9(dateKey, 15),
    reminder_7d: daysBeforeAt9(dateKey, 7),
    reminder_1d: daysBeforeAt9(dateKey, 1),
    reminder_4h: new Date(start.getTime() - 4 * HOUR),
    thank_you: new Date(end.getTime() + 1 * HOUR),
  };
}

/**
 * The rung that supersedes each pre-event reminder: once it is due, a stale earlier
 * reminder is pointless (and noisy) — a late 30d must never go out beside the 4h.
 */
const NEXT_RUNG: Record<Exclude<StaffReminderType, 'thank_you'>, StaffReminderType | 'start'> = {
  reminder_30d: 'reminder_15d',
  reminder_15d: 'reminder_7d',
  reminder_7d: 'reminder_1d',
  reminder_1d: 'reminder_4h',
  reminder_4h: 'start',
};

/**
 * Is a reminder whose correct time has passed still worth sending at `now`?
 * Pre-event reminders: only until the NEXT rung of the ladder is due
 * (30d → until the 15d instant, 15d → 7d, 7d → 1d, 1d → reminder_4h, reminder_4h → event start).
 * thank_you: from window end until end + 24h.
 */
export function stillMeaningful(type: StaffReminderType, dateKey: string, slot: SlotType, now: Date): boolean {
  const { start, end } = eventWindow(dateKey, slot);
  const t = now.getTime();
  if (type === 'thank_you') return t >= end.getTime() && t < end.getTime() + THANK_YOU_GRACE_MS;
  const next = NEXT_RUNG[type];
  if (!next) return false; // unknown type — never assert it is worth sending
  const deadline = next === 'start' ? start : staffReminderSchedule(dateKey, slot)[next];
  return t < deadline.getTime();
}
